import { Table, Typography } from "antd";
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import axios from "api/axios";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import { formatDataGeneral, getAttributeValue, getFileLinkIfExist, isDateInSelectedYear } from "utils/utils";
import { renderDpatEvidenceLink, scoreOnlyIfDocumentLinksComplete } from "utils/renderDpatEvidenceLink";
import { documentsColumn } from "utils/tableColums";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";

const DOCUMENT_HUB_FILE_ELEMENT = "MjPmcbueEdU";
const MINUTES_DATA_ELEMENT = "LAe1t59jYNT";

const DOCUMENT_HUB_TYPES = [
  "Monitoring, Building and Inspection  Reports",
  "Local Planning Reports",
];

const TECHNICAL_SUB_COMMITTEE_MEETING_TYPES = [
  "Technical Sub-Committee",
  "Technical Sub-committee",
  "Technical Sub Committee",
];

function fileUrl(eventId, dataElementUid) {
  return `https://dddp.gov.gh/api/events/files?eventUid=${eventId}&dataElementUid=${dataElementUid}`;
}

function isMeetingInYear(meeting, year) {
  const meetingDate = getAttributeValue("DPAT | Meeting Date", meeting);
  if (!meetingDate || meetingDate === "N/A") {
    return false;
  }

  return isDateInSelectedYear(meetingDate, year);
}

function buildDocumentHubLink(documents, title) {
  const documentData = documents?.data;
  const documentReports = documents?.reports;
  const matchedDocs = formatDataGeneral(
    documentData,
    "DPAT | M&E Tool Type",
    title,
  ) || [];

  let documentLink = "";
  if (matchedDocs.length > 0) {
    documentLink = getFileLinkIfExist(
      documentReports,
      DOCUMENT_HUB_FILE_ELEMENT,
      matchedDocs[0]?.trackedEntity,
    );
  }

  return {
    title,
    link: documentLink
      ? fileUrl(documentLink, DOCUMENT_HUB_FILE_ELEMENT)
      : "Not Uploaded",
  };
}

function buildMeetingMinutesLink(meetings, meetingTypes, title, year) {
  const meetingList = meetings?.meetings || [];
  const meetingReports = meetings?.reports || [];
  const types = Array.isArray(meetingTypes) ? meetingTypes : [meetingTypes];

  for (const meetingType of types) {
    const matchedMeetings = formatDataGeneral(meetingList, "DPAT | Meeting Type", meetingType)
      ?.filter((meeting) => isMeetingInYear(meeting, year)) || [];

    for (const meeting of matchedMeetings) {
      const minuteLink = getFileLinkIfExist(
        meetingReports,
        MINUTES_DATA_ELEMENT,
        meeting.trackedEntity,
      );

      if (minuteLink) {
        return {
          title,
          link: fileUrl(minuteLink, MINUTES_DATA_ELEMENT),
        };
      }
    }
  }

  return { title, link: "Not Uploaded" };
}

function buildPermitProcessingDocumentLinks(documents, meetings, year) {
  const links = DOCUMENT_HUB_TYPES.map((title) => buildDocumentHubLink(documents, title));

  links.push(
    buildMeetingMinutesLink(
      meetings,
      "Spatial Planning Committee (SPC)",
      "Minutes of Spatial Planning Committee Meeting",
      year,
    ),
  );

  links.push(
    buildMeetingMinutesLink(
      meetings,
      TECHNICAL_SUB_COMMITTEE_MEETING_TYPES,
      "Minutes of Technical Sub-committee Meeting",
      year,
    ),
  );

  return links;
}

const PermitProcessingIssuance = forwardRef(({
  year,
  districtId,
  documents,
  meetings,
  hideComment,
  savedAssessmentData,
  assessmentStartLoading,
  isAssessmentStarted,
}, ref) => {
  const resolvedYear =
    year != null && typeof year === "object" && !Array.isArray(year)
      ? year.value ?? year.label
      : year;
  const analyticsYear = resolvedYear ?? new Date().getFullYear();
  const ouId =
    typeof districtId === "string"
      ? districtId
      : districtId?.value?.id ?? districtId?.id ?? "";

  const [data, setData] = useState([]);
  const [documentLinks, setDocumentLinks] = useState([]);
  const [scorei, setScorei] = useState(0);
  const [scoreii, setScoreii] = useState(0);
  const [maxScore, setMaxScore] = useState(3);

  const { Title } = Typography;

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "SDI3",
      area: "Physical and Spatial Planning Services",
      maxScore,
      data,
      links: documentLinks,
      scorei,
      scoreii,
    }),
  }));

  const DPAT_SLUG = "sdi3.0-3.2";

  const dataColumn = [
    { title: `No. of Building Permit Requests Received in ${analyticsYear} (A)`, dataIndex: "permitRequest", key: "permitRequest" },
    { title: `No. of Building Permit Requests Processed & Issued in ${analyticsYear} (B)`, dataIndex: "permitIssued", key: "permitIssued" },
    { title: "No. of approved permits traced to Local Plans (C)", dataIndex: "permitTraced", key: "permitTraced" },
  ];

  const documentTableColumns = useMemo(
    () => documentsColumn.map((col) =>
      col.dataIndex === "link"
        ? {
          ...col,
          render: (value) => renderDpatEvidenceLink(value, "View Document"),
        }
        : col
    ),
    [],
  );

  const getIndicatorsData = useCallback(() => {
    const links = buildPermitProcessingDocumentLinks(documents, meetings, analyticsYear);
    setDocumentLinks(links);

    if (!ouId) {
      return;
    }

    axios.get(`/analytics.json?dimension=dx:I4F0ubwWfun;IXslJdAFMYW;m8yHdlshnwa&dimension=ou:LEVEL-3;${ouId}&filter=pe:${analyticsYear}-01-01;${analyticsYear}-12-31`)
      .then((res) => {
        const rows = res.data?.rows;
        const length = rows?.length;

        if (length > 0) {
          const temp = {
            permitRequest: rows[0][2],
            permitIssued: length > 1 ? rows[1][2] : 0,
            permitTraced: length > 2 ? rows[2][2] : 0,
          };

          setData([temp]);

          const scoreIValue = length > 1 && rows[0][2] == rows[1][2] ? 2 : 0;
          const scoreIIValue = length > 2 && rows[1][2] == rows[2][2] ? 1 : 0;
          setScorei(scoreOnlyIfDocumentLinksComplete(links, scoreIValue));
          setScoreii(scoreOnlyIfDocumentLinksComplete(links, scoreIIValue));
        } else {
          setData([]);
          setScorei(0);
          setScoreii(0);
        }
      })
      .catch((err) => console.log(err));
  }, [ouId, analyticsYear, documents, meetings]);

  useDpatIndicatorDataSource({
    isAssessmentStarted,
    savedAssessmentData,
    assessmentStartLoading,
    loadFromDhis2: getIndicatorsData,
    applySnapshot: (snap) => {
      if (snap.data) setData(snap.data);
      if (snap.links) setDocumentLinks(snap.links);
      if (snap.scorei != null) setScorei(snap.scorei);
      if (snap.scoreii != null) setScoreii(snap.scoreii);
      if (snap.maxScore != null) setMaxScore(snap.maxScore);
    },
    deps: [year, districtId, documents, meetings, getIndicatorsData],
  });

  return (
    <Comment
      data={data}
      year={analyticsYear}
      districtId={ouId}
      tableCommentedId={`sdi3.0-3.2-${analyticsYear}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
          <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={analyticsYear}
            slug={DPAT_SLUG}
            mainTitleStyle={{ marginTop: "30px" }}
          />

          <IndicatorScoreSection>
          <MaxScoreDisplay score={maxScore} />
                    <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 3.0-3.2i Actual Score:" score={scorei} />
          <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 3.0-3.2ii Actual Score:" score={scoreii} />
          </IndicatorScoreSection>
          <Title level={4} style={{ marginTop: "20px" }}>Evidence of Processing & Issuance of Building Permit
            Requests</Title>
          <Table
            columns={dataColumn}
            dataSource={data || []}
            pagination={false}
            bordered
          />

          <Title level={5} style={{ marginTop: "20px" }}>Evidence of Attached Documents</Title>
          <Table
            columns={documentTableColumns}
            dataSource={documentLinks}
            rowKey="title"
            pagination={false}
            bordered
          />

            {!hideComment && renderCommentFeedbackFooter()}
          </DpatSubIndicatorSection>
      )}
    </Comment>
  );
});

export default PermitProcessingIssuance;
